// @flow
import { useRef, useCallback, useState, useEffect, useMemo } from 'react'
import type {
	UseLocalTimersFunctionType,
	UseWatchTimersFunctionType,
	TimerProgressMap,
	TimerProgress,
} from '@mission.io/mission-player'
import { forEachEntry } from '../utility'
import { useMissionStateSelector } from './customHooks'
import { getTimers, isPaused } from './missionState/missionStats'
import { areNumbersInDifferentBinsFromZero } from '../utility/functions'

/**
 * useAnimationFrame - run the callback on every animation frame (this hook does not cause a rerender, if a rerender should occur, it must be triggered in the callback)
 * based off of code from article: https://css-tricks.com/using-requestanimationframe-with-react-hooks/
 *
 * @param  {()=>void} callback  - the function to call on each animation frame
 */
function useAnimationFrame(callback?: ?() => void) {
	const requestRef = useRef()

	const animate = useCallback(
		time => {
			if (callback) {
				callback()
				requestRef.current = requestAnimationFrame(animate)
			}
		},
		[callback]
	)

	useEffect(() => {
		requestRef.current = requestAnimationFrame(animate)
		return () => {
			if (requestRef.current) {
				cancelAnimationFrame(requestRef.current)
			}
		}
	}, [animate])
}

type TimerData = {
	callback: () => any,
	timeRemaining: number,
	totalTime: number,
	startTime: number,
	timeoutId: ?TimeoutID,
	pausesWithMission: boolean,
}

/**
 * Returns a set of functions that can create and delete timers that run locally, ie on the client. Any timers created by the returned
 * addTimer function will be paused when the argument to this function is true, and will be destroyed when the component unmounts
 */
export const useLocalTimers: UseLocalTimersFunctionType = (
	isMissionPaused: boolean
) => {
	const timersRef = useRef<{ [localTimerKey: string]: TimerData }>({})
	const isMounted = useRef(true)
	const nextKey = useRef(0)
	const isMissionPausedRef = useRef(isMissionPaused)

	const functions = useMemo(() => {
		const clearTimer = (key?: ?string) => {
			if (key == null) {
				return
			}
			const timerData = timersRef.current[key]
			if (!timerData) {
				return
			}
			if (timerData.timeoutId) {
				clearTimeout(timerData.timeoutId)
			}
			delete timersRef.current[key]
		}

		const clearAllTimers = () => {
			Object.keys(timersRef.current).forEach(clearTimer)
		}

		const addTimer = (
			callback: () => any,
			elapse: number,
			pausesWithMission?: boolean = true,
			timeAlreadyPassed?: number = 0
		) => {
			if (!isMounted.current) {
				return null
			}
			const key = String(nextKey.current++)
			timersRef.current[key] = {
				callback,
				totalTime: elapse + timeAlreadyPassed,
				timeRemaining: elapse,
				pausesWithMission,
				startTime: Date.now(),
				timeoutId: null,
			}
			if (!(pausesWithMission && isMissionPausedRef.current)) {
				timersRef.current[key].timeoutId = setTimeout(() => {
					clearTimer(key)
					callback()
				}, elapse)
			}
			return key
		}

		const getProgress = (key?: ?string): ?TimerProgress => {
			if (!key) {
				return
			}
			const timer = timersRef.current[key]
			if (!timer) {
				return
			}
			const isPaused = !timer.timeoutId
			return {
				id: key,
				totalTime: timer.totalTime,
				timeLeft: isPaused
					? timer.timeRemaining
					: timer.timeRemaining - (Date.now() - timer.startTime),
				isPaused,
			}
		}
		return { clearTimer, clearAllTimers, addTimer, getProgress }
	}, [])

	useEffect(() => {
		const currentTime = Date.now()
		isMissionPausedRef.current = isMissionPaused
		if (isMissionPaused) {
			forEachEntry(timersRef.current, ([key, timerData]) => {
				if (timerData.pausesWithMission) {
					if (timerData.timeoutId) {
						clearTimeout(timerData.timeoutId)
						timerData.timeoutId = null
					}
					timerData.timeRemaining -= currentTime - timerData.startTime
				}
			})
		} else {
			forEachEntry(timersRef.current, ([key, timerData]) => {
				if (timerData.pausesWithMission) {
					if (timerData.timeoutId) {
						return
					}
					timerData.startTime = currentTime
					timerData.timeoutId = setTimeout(() => {
						functions.clearTimer(key)
						timerData.callback()
					}, timerData.timeRemaining)
				}
			})
		}
	}, [isMissionPaused, functions])

	useEffect(() => {
		isMounted.current = true // handle react 18 running the effect twice on mount in strict mode
		return () => {
			functions.clearAllTimers()
			isMounted.current = false
		}
	}, [functions])

	return functions
}

/**
 * useWatchTimers - get the progress of every timer passed in, and keep the progress up to date on every frame
 *
 * @param  {?Array<?string>} deltaTimers - an array of all the timers in the delta clock to watch
 * @param  {?Array<?string>} localTimers - an array of the keys of all the local timers to watch
 * @param  {?(localTimerId?: ?string) => ?TimerProgress} getLocalTimerProgress - a function to resolve the progress of the local timers
 * @param  {?number} updateRate - the number of milliseconds before updating the timers
 * @returns {{[timerId: string]: TimerProgress}} - a mapping of the timerIds, to their progress (updated every frame)
 */
export const useWatchTimers: UseWatchTimersFunctionType = ({
	deltaClockTimers,
	localTimers,
	getLocalTimerProgress,
	updateRate,
}) => {
	const previousUpdateTime = useRef<number>(Date.now())
	const updateTimers = useRef<() => TimerProgressMap>(() => {
		return {}
	})

	const _updateRate = useRef(updateRate)
	_updateRate.current = updateRate

	updateTimers.current = () => {
		const timers = {}
		if (localTimers && getLocalTimerProgress) {
			localTimers.forEach(timerId => {
				if (!timerId) {
					return
				}
				let localTimerProgress = getLocalTimerProgress(timerId)
				if (localTimerProgress) {
					timers[timerId] = localTimerProgress
				}
			})
		}

		if (deltaClockTimers) {
			Object.keys(deltaClockTimers).forEach(timerId => {
				const timerData = deltaClockTimers[timerId]
				if (!timerData) {
					return
				}
				timers[timerId] = {
					timeLeft: timerData.delayLeft,
					totalTime: timerData.originalDelay,
					id: timerId,
					isPaused: timerData.isPaused,
				}
			})
		}
		return timers
	}

	const [timers, setTimers] = useState<TimerProgressMap>(updateTimers.current)

	const updateTimersCallback = useCallback(() => {
		const updateRate = _updateRate.current
		if (
			!updateRate ||
			areNumbersInDifferentBinsFromZero(
				Date.now(),
				previousUpdateTime.current,
				updateRate
			)
		) {
			setTimers(updateTimers.current())
			previousUpdateTime.current = Date.now()
		}
	}, [])

	useAnimationFrame(updateRate === Infinity ? null : updateTimersCallback)
	return timers
}

/**
 * Returns a timer progress object for the timer with the given timer id
 * @param {?string} selectorForTimerId
 * @returns
 */
export const useTimer = (timerId: ?string): ?TimerProgress => {
	const missionIsPaused = useMissionStateSelector(isPaused)
	const deltaClockTimers = useMissionStateSelector(getTimers([timerId]))
	const timer = useWatchTimers({
		deltaClockTimers,
		updateRate: missionIsPaused ? Infinity : 1000,
	})[timerId]
	return timer
}
